/**
 * useDepreciation
 *
 * Owns the depreciation / asset management state that was previously class
 * state on FormViewer:
 *
 *   handleAddAsset(activity)          — select asset and open modal
 *   deleteAsset(id)                   — delete via calc engine
 *   reassignAsset(astID, actvID)      — reassign via calc engine
 *   addNewAsset(id,type,desc,date,c)  — create new asset via calc engine
 *   viewAsset(id)                     — view asset via calc engine
 *   toggleAssetSnackbar(msg, toggle)  — show/hide the asset snackbar
 *   toggleDeleteDialog(form, desc)    — toggle the delete-form confirm dialog
 *   toggleAssetDeleteDialog()         — toggle the delete-asset confirm dialog
 *   toggleDeletePkgDialog(pkg, desc)  — toggle the delete-pkg confirm dialog
 *   handleDeleteForm(form)            — confirm and delete a form
 *   handleDeleteActivityForm(form)    — confirm and delete an activity form
 *   handleDeletePkg(pkg)              — confirm and delete a package
 *
 * State returned for render:
 *   selectedAsset, reassignAssetList
 *   assetSnackbar, assetSnackbarMsg
 *   confirmDeleteDialogOpen, removeForm, removeFormID, removeFormDesc,
 *   removeFormDlg, removeFormIrremovable
 *   confirmAssetDelete
 *   confirmPkgDelete, removePkgId, removePkgDesc
 *
 * Callers must supply:
 *   calcAPIRef             — ref to CalcAPI instance
 *   formNavRef             — ref to formNav hook (for getTaxForm navigation)
 *   onToggleAddFormList    — Redux action
 *   onToggleAddAssetModal  — Redux action
 */
import { useState, useCallback } from 'react';
import { FORM_NAMES, IRREMOVABLE_FORMS } from '~/app/constants';

const useDepreciation = ({
  calcAPIRef,
  formNavRef,
  onToggleAddFormList,
  onToggleAddAssetModal,
}) => {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [reassignAssetList, setReassignAssetList] = useState([]);
  const [assetSnackbar, setAssetSnackbar] = useState(false);
  const [assetSnackbarMsg, setAssetSnackbarMsg] = useState('');
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [removeForm, setRemoveForm] = useState(null);
  const [removeFormID, setRemoveFormID] = useState(null);
  const [removeFormDesc, setRemoveFormDesc] = useState(null);
  const [removeFormDlg, setRemoveFormDlg] = useState(null);
  const [removeFormIrremovable, setRemoveFormIrremovable] = useState(false);
  const [confirmAssetDelete, setConfirmAssetDelete] = useState(false);
  const [confirmPkgDelete, setConfirmPkgDelete] = useState(false);
  const [removePkgId, setRemovePkgId] = useState(null);
  const [removePkgDesc, setRemovePkgDesc] = useState(null);

  const handleAddAsset = useCallback(
    activity => {
      setSelectedAsset(activity);
      onToggleAddFormList();
      onToggleAddAssetModal(true);
    },
    [onToggleAddFormList, onToggleAddAssetModal],
  );

  const deleteAsset = useCallback(
    id => calcAPIRef.current?.ReturnDeleteAsset(id),
    [], // eslint-disable-line
  );
  const reassignAsset = useCallback(
    (astID, actvID) => calcAPIRef.current?.ReturnReassignAsset(astID, actvID),
    [calcAPIRef],
  );
  const addNewAsset = useCallback(
    (id, type, desc, date, assetCode) =>
      calcAPIRef.current?.ReturnNewAsset(id, type, desc, date, assetCode),
    [calcAPIRef],
  );
  const viewAsset = useCallback(id => calcAPIRef.current?.ReturnViewAsset(id), [calcAPIRef]);

  const toggleAssetSnackbar = useCallback((message, toggle) => {
    setAssetSnackbar(toggle);
    setAssetSnackbarMsg(message);
  }, []);

  const toggleDeleteDialog = useCallback((form, desc) => {
    let dlg = (desc ?? '') + ' has been selected for removal. This cannot be undone.';
    let isIrremovable = false;
    if (IRREMOVABLE_FORMS.includes(form?.var)) {
      dlg = 'Cannot Delete 1040 Schedule. Entries on this form must be manually removed.';
      isIrremovable = true;
    }
    setRemoveForm(form);
    setRemoveFormID(form === null ? null : form.var);
    setRemoveFormDesc(desc);
    setRemoveFormDlg(dlg);
    setRemoveFormIrremovable(isIrremovable);
    setConfirmDeleteDialogOpen(v => !v);
  }, []);

  const toggleAssetDeleteDialog = useCallback(() => setConfirmAssetDelete(v => !v), []);

  const toggleDeletePkgDialog = useCallback((pkg, desc) => {
    setRemovePkgId(pkg);
    setRemovePkgDesc(desc);
    setConfirmPkgDelete(v => !v);
  }, []);

  const handleDeleteForm = useCallback(
    form => {
      setConfirmDeleteDialogOpen(false);
      if (form.actvForm !== '') {
        setConfirmAssetDelete(v => !v);
      } else {
        formNavRef.current?.getTaxForm(FORM_NAMES.CDS, '', '', '');
        calcAPIRef.current?.ReturnRemoveForm(form.var);
      }
    },
    [calcAPIRef, formNavRef],
  );

  const handleDeleteActivityForm = useCallback(
    form => {
      setConfirmAssetDelete(false);
      formNavRef.current?.getTaxForm(FORM_NAMES.CDS, '', '', '');
      calcAPIRef.current?.ReturnRemoveForm(form.var);
    },
    [calcAPIRef, formNavRef],
  );

  const handleDeletePkg = useCallback(
    pkg => {
      setConfirmPkgDelete(false);
      formNavRef.current?.getTaxForm(FORM_NAMES.CDS, '', '', '');
      calcAPIRef.current?.ReturnRemovePackage(pkg);
    },
    [calcAPIRef, formNavRef],
  );

  return {
    selectedAsset,
    setSelectedAsset,
    reassignAssetList,
    setReassignAssetList,
    assetSnackbar,
    assetSnackbarMsg,
    confirmDeleteDialogOpen,
    setConfirmDeleteDialogOpen,
    removeForm,
    removeFormID,
    removeFormDesc,
    removeFormDlg,
    removeFormIrremovable,
    confirmAssetDelete,
    setConfirmAssetDelete,
    confirmPkgDelete,
    removePkgId,
    removePkgDesc,
    handleAddAsset,
    deleteAsset,
    reassignAsset,
    addNewAsset,
    viewAsset,
    toggleAssetSnackbar,
    toggleDeleteDialog,
    toggleAssetDeleteDialog,
    toggleDeletePkgDialog,
    handleDeleteForm,
    handleDeleteActivityForm,
    handleDeletePkg,
  };
};

export default useDepreciation;
